import type {
  DirectTaskRelationsResponse,
  TaskArtifactsView,
  TaskResultsView,
} from '@/lib/api/generated/model';
import { TASK_TABS, type TaskTab } from './searchParams';

/**
 * Which tabs the drawer offers, and what the Results tab counts (spec 095).
 *
 * Pure, so the drawer's one structural decision — whether a fourth tab exists
 * at all — is a function of the results payload and nothing else, and can be
 * tested without rendering. The three base tabs are always offered; Results
 * joins them only once the task has produced something (FR-001, FR-002). A
 * "Results" tab on a task that has produced nothing is the placeholder tab
 * spec 063 FR-034 was right to forbid.
 */

const BASE_TABS: readonly TaskTab[] = TASK_TABS.filter(
  (tab) => tab !== 'results',
);

/**
 * The task's artifact read as the tab decisions see it (spec 119, research
 * R2): the view once answered, `'pending'` until then, `'failed'` if it could
 * not be read. A failure counts as "none" everywhere here — the section says
 * so itself — so a backend without the endpoint behaves exactly as 095 did.
 *
 * Every function below defaults it to "none", which is what every spec 095
 * caller meant by not passing it.
 */
export type ArtifactsRead = TaskArtifactsView | 'pending' | 'failed';

const NO_ARTIFACTS: ArtifactsRead = { artifacts: [] };

/** The read as the tab functions take it, from what `useTaskArtifacts` returns. */
export function artifactsReadOf(read: {
  artifacts: TaskArtifactsView | undefined;
  loadFailed: boolean;
}): ArtifactsRead {
  if (read.artifacts) return read.artifacts;
  return read.loadFailed ? 'failed' : 'pending';
}

function artifactCount(artifacts: ArtifactsRead): number {
  return typeof artifacts === 'string' ? 0 : (artifacts.artifacts ?? []).length;
}

/**
 * Whether any run recorded a result, any file change is attributed, or the
 * task created anything on the forge (spec 119 FR-011).
 */
export function hasResults(
  results: TaskResultsView | undefined,
  artifacts: ArtifactsRead = NO_ARTIFACTS,
): boolean {
  if (artifactCount(artifacts) > 0) return true;
  if (!results) return false;
  const executions = results.executions ?? [];
  const files = results.files ?? [];
  return executions.some((run) => run.result !== null) || files.length > 0;
}

/**
 * Verified links across every run plus the distinct paths the task changed
 * (FR-003). `null` rather than `0`: the tab is not offered at zero, and a
 * count the strip would print as "0" is not a count (see `tabCount`).
 */
export function resultsCount(
  results: TaskResultsView | undefined,
  artifacts: ArtifactsRead = NO_ARTIFACTS,
): number | null {
  // Plus what the task created (spec 119 FR-012). No deduplication against the
  // links: for a recorded run the backend already omits the merge requests the
  // model reported, and an older run has no artifacts (research R6).
  const created = artifactCount(artifacts);
  if (!results) return created > 0 ? created : null;
  const links = (results.executions ?? []).reduce(
    (total, run) => total + (run.result?.links.length ?? 0),
    0,
  );
  const paths = new Set((results.files ?? []).map((file) => file.path)).size;
  const count = links + paths + created;
  return count > 0 ? count : null;
}

/** The tabs to draw, in strip order. */
/**
 * How many connections the Relations tab has to show, or null for none.
 *
 * The task's own connections only. Counting everything reachable would mean
 * running the transitive traversal on every drawer open, which is the read this
 * feature keeps behind a control (spec 100 FR-003, SC-004).
 *
 * Null rather than 0 because the tab strip renders no number for a null count,
 * which is what a task with nothing connected should look like: a tab you can
 * still open to add the first one, not a tab shouting zero.
 */
export function relationsCount(
  relations: DirectTaskRelationsResponse | undefined,
): number | null {
  const count = relations?.relations.length ?? 0;
  return count > 0 ? count : null;
}

export function offeredTabs(
  results: TaskResultsView | undefined,
  artifacts: ArtifactsRead = NO_ARTIFACTS,
): readonly TaskTab[] {
  return hasResults(results, artifacts) ? [...BASE_TABS, 'results'] : BASE_TABS;
}

/** What the strip selects, or `'pending'` while the first read is still out. */
export type Landing = TaskTab | 'pending';

/**
 * Which tab the drawer opens on (FR-004 – FR-006).
 *
 * An address that names a tab means that tab, whatever the task holds: a
 * shared link says what it says. Otherwise the drawer waits for the first
 * results read — `'pending'` — and then lands on Results when the task has
 * produced something and nothing is running, and on Details in every other
 * case. Waiting is the point: opening on one tab and switching to another
 * underneath the reader is the one thing FR-004 forbids.
 */
export function landingTab(
  explicit: TaskTab | undefined,
  results: TaskResultsView | undefined,
  live: boolean,
  artifacts: ArtifactsRead = NO_ARTIFACTS,
): Landing {
  if (explicit) return explicit;
  // Both first reads, not either (spec 119 FR-013): landing on Details and
  // then offering Results when the artifact read arrives would be the switch
  // underneath the reader that 095 FR-004 forbids.
  if (results === undefined || artifacts === 'pending') return 'pending';
  return hasResults(results, artifacts) && !live ? 'results' : 'details';
}
